import * as React from 'react';
import { getItem } from '@/redux/modules/item';
import Slider from 'react-slick/lib/slider';
import styled from 'styled-components';
import Thumbnail from './Thumbnail';
import { useSelector } from 'react-redux';

const THUMBNAIL_SHOW_COUNT = 7;

type Props = {
    activeIndex: number;
    className?: string;
    itemId: number;
    onClick: Function;
};

const ThumbnailScroller = ({ activeIndex, className, itemId, onClick }) => {
    const { photos = [] } = useSelector((state) => getItem(state, itemId));
    const [loaded, setLoaded] = React.useState(false);

    const settings = {
        dots: false,
        infinite: false,
        lazyLoad: false,
        nextArrow: <StyledDownArrow />,
        prevArrow: <StyledUpArrow />,
        slidesToScroll: THUMBNAIL_SHOW_COUNT,
        slidesToShow: Math.min(photos.length, THUMBNAIL_SHOW_COUNT),
        vertical: true,
        verticalSwiping: true,
    };

    const showSlider = photos.length > THUMBNAIL_SHOW_COUNT && loaded;

    const mapThumbnails = React.useCallback(
        (photo, index) => {
            return (
                <StyledThumbnail
                    $slider={showSlider}
                    active={index === activeIndex}
                    alt={`item-${itemId}=${photo}`}
                    imgNumber={photo}
                    itemId={itemId}
                    key={`${itemId}=${photo}`}
                    onClick={() => onClick(index)}
                />
            );
        },
        [activeIndex, itemId, onClick, showSlider]
    );

    React.useEffect(() => {
        window.setTimeout(() => setLoaded(true), 100);
    });

    //TODO: this is here until we can figure out how to get the slider to work properly on render
    let shortPhotos = [...photos];
    if (!loaded) {
        shortPhotos = shortPhotos.slice(0, THUMBNAIL_SHOW_COUNT);
    }

    return (
        <StyledThumbnailScroller $slider={showSlider} className={className}>
            {showSlider ? (
                <StyledSlider {...settings}>{photos.map(mapThumbnails)}</StyledSlider>
            ) : (
                shortPhotos.map(mapThumbnails)
            )}
        </StyledThumbnailScroller>
    );
};

export default React.memo<Props>(ThumbnailScroller);

interface SliderProps {
    $slider: boolean;
}

const StyledThumbnailScroller = styled.div<SliderProps>`
    display: block;
    flex: 0 0 60px;
    position: relative;
    margin-right: 44px;
    height: 490px;
    margin-top: ${({ $slider }) => ($slider ? '20px' : '21px')};

    .slick-vertical {
        height: 490px;
    }

    .slick-arrow {
        opacity: 1;
        transition: opacity 0.25s;
    }

    .slick-arrow.slick-disabled {
        opacity: 0.3;
        cursor: not-allowed;
    }
`;

const StyledSlider = styled(Slider)`
    width: 60px;
    height: 490px;

    && *:focus {
        outline: 0;
    }
`;

const StyledThumbnail = styled(Thumbnail)<SliderProps>`
    margin: ${({ $slider }) => ($slider ? '0' : '0 1px 14px')};
`;

const StyledUpArrow = styled.div`
    && {
        width: 0;
        height: 0;
        position: absolute;
        top: -20px;
        right: auto;
        transform: translateY(-50%);
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-bottom: 10px solid ${({ theme }) => theme.colors.grey100};
        margin: 5px auto;
        left: calc(50% - 10px);

        &::before {
            content: none;
        }
    }
`;

const StyledDownArrow = styled.div`
    && {
        width: 0;
        height: 0;
        position: absolute;
        bottom: -20px;
        top: auto;
        right: auto;
        transform: translateY(-50%);
        border-left: 10px solid transparent;
        border-right: 10px solid transparent;
        border-top: 10px solid ${({ theme }) => theme.colors.grey100};
        margin: 5px auto;
        left: calc(50% - 10px);

        &::before {
            content: none;
        }
    }
`;
